// @react
import React, {
  Dispatch,
  ReactElement,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'
// @graphql
import { useMutation, useQuery } from '@apollo/client'
import {
  GetUserGroupsQuery,
  GetUserGroupsQueryVariables,
  UserLeaveUserGroupMutation,
  UserLeaveUserGroupMutationVariables,
} from 'lib/graphql/graphqlTypes'
import { GET_USER_GROUPS } from 'graphql/queries/userNetwork.query'
import { USER_LEAVE_GROUP } from 'graphql/mutations/userNetwork.mutation'
// @libraries
import { makeStyles, Theme } from '@material-ui/core/styles'
// @common
import usePagination from 'modules/common/hooks/usePagination'
import { RouteNamesExternal } from 'constants/routeNamesExternal'
// @components
import colors from '@bit/atd.web.colors'
import Grid from '@bit/atd.web.grid'
import Icon from '@bit/atd.web.icons'
import Button from '@bit/atd.web.button'
import FollowCard from '__COMPONENTS_TO_MIGRATE/CardNew/Variants/Follow'
// @module components
import PaginationWrapper from 'modules/common/components/PaginationWrapper'
import QueryError from 'modules/common/components/Errors/QueryError'
import { SnackbarContext } from 'modules/common/context/snackbar'
import { SnackbarTypes } from '@bit/atd.web.snackbar'
import { Typography } from '@material-ui/core'

/**
 * 5.0.6.3 Profile - Network - Recommended Users
 * https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=3247%3A5776
 *
 *  fields:
 *  user  lastName
 *  user  firstName
 *  user  staff
 *  user  imageUrl
 *  user  jobTitle
 */

type PropsType = {
  setCount: Dispatch<SetStateAction<number>>
}

const useStyles = makeStyles((theme: Theme) => ({
  cards: {
    '& .MuiGrid-item': {
      [theme.breakpoints.down('sm')]: {
        '@media (min-width: 375px)': {
          flexGrow: 0,
          maxWidth: '50%',
          flexBasis: '50%',
        },
        '@media (min-width: 670px)': {
          flexGrow: 0,
          maxWidth: '33.333%',
          flexBasis: '33.333%',
        },
      },
    },
  },
  allGroupsLink: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
    padding: '6px 0',
    color: colors.ctaSecondary,
    transition: 'all 0.3s ease',
    textDecoration: 'none',
    '&:hover': {
      color: colors.ctaPrimary,
    },
    '& span:first-child': {
      fontWeight: 600,
      fontSize: '16px',
      lineHeight: '20px',
      marginRight: '8px',
    },
  },
}))

const Groups = ({ setCount }: PropsType): ReactElement => {
  const { Pagination, paginationProps } = usePagination()

  /******************************************
   * STATE
   ******************************************/
  const [isSubmitting, setIsMutationSubmitting] = useState<string | null>(null)

  /******************************************
   * GQL HOOKS
   ******************************************/
  const { loading: isLoading, error: isError, data, refetch } = useQuery<
    GetUserGroupsQuery,
    GetUserGroupsQueryVariables
  >(GET_USER_GROUPS, {}) // @todo expand query with page variable (for pagination)

  const [leaveGroup] = useMutation<
    UserLeaveUserGroupMutation,
    UserLeaveUserGroupMutationVariables
  >(USER_LEAVE_GROUP)

  /******************************************
   * HOOKS
   ******************************************/
  const classes = useStyles()
  const snackbar = useContext(SnackbarContext)
  useEffect(() => {
    setCount && setCount(data?.userGroups.meta?.count || 0)
  }, [setCount, data])

  /******************************************
   * FUNCTIONS
   ******************************************/

  /**
   *
   * @param groupId
   */
  const leaveGroupAction = async (groupId: string) => {
    setIsMutationSubmitting(groupId)
    const { errors: mutationErrors } = await leaveGroup({
      variables: {
        input: {
          groupId,
        },
      },
    })

    if (!mutationErrors) {
      snackbar.setMessage({
        type: SnackbarTypes.SUCCESS,
        message: `Successfully left group`,
        show: true,
      })
      await refetch()
    } else {
      console.log('unfollowTopicAction error', mutationErrors)
      snackbar.setMessage({
        type: SnackbarTypes.ERROR,
        message: `There was a problem leaving the group`,
        show: true,
      })
    }
    setIsMutationSubmitting(null)
  }

  /******************************************
   * RENDER
   ******************************************/

  if (isLoading) {
    return <span>Loading...</span>
  }

  if (isError || !data) {
    return <QueryError />
  }

  return (
    <>
      <Grid container style={{ marginBottom: '32px' }}>
        <Grid item>
          <Button
            variant="secondary"
            size="medium"
            href={RouteNamesExternal.ATD_GROUPS}
          >
            <>
              <Typography component={'p'}>
                <strong>View All Groups &nbsp;</strong>
              </Typography>
              <Icon glyph="arrow-right" size={16} color={colors.ctaPrimary} />
            </>
          </Button>
        </Grid>
      </Grid>

      <Grid className={classes.cards} container spacing={3}>
        {data?.userGroups?.data?.map((item, index) => {
          return (
            <Grid key={`${item.__typename}_${item.id}`} xs={12} sm={3} item>
              <FollowCard
                following
                avatar={{
                  size: 48,
                  src: '',
                }}
                title={item?.title!}
                btnLabel={'Leave Group'}
                isLoading={isSubmitting === item.id}
                layout={'auto'}
                onButtonClick={() => item && leaveGroupAction(item.id)}
              />
            </Grid>
          )
        })}
      </Grid>

      <PaginationWrapper>
        <Pagination
          {...paginationProps}
          count={data.userGroups.meta.totalPages}
        />
      </PaginationWrapper>
    </>
  )
}

export default Groups
